/**
 * GG Cart Drawer — slide-out with tier-aware upsell.
 *
 * Dawn product-form expects: getSectionsToRender(), setActiveElement(),
 * renderContents(response), open(), and is-empty class handling.
 *
 * On any cart change we refetch /cart.js and rebuild the body + footer.
 * Tier-aware upsell box appears based on cart state:
 *   subscribed 3-pack → push 10-pack subscription (in-cart swap, free shipping, $8.50/pouch)
 *   one-time, <= 1500 → push 3-pack (in-cart swap)
 *   one-time, <= 4000 → push 10-pack (in-cart swap, free shipping, $10/pouch)
 *   otherwise         → no cart-level upsell; per-line sub toggle handles intent
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.panel = this.querySelector('.ggd-panel');
      this.body = this.querySelector('[data-drawer-body]');
      this.foot = this.querySelector('[data-drawer-foot]');
      this.totalEl = this.querySelector('[data-cart-total]');
      this.activeElement = null;
      this._lastCart = null;
      this._bind();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this._warmCache(), { timeout: 1500 });
      } else {
        setTimeout(() => this._warmCache(), 800);
      }
    }

    _warmCache() {
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => { this._lastCart = cart; this._patchHeaderCount(cart); })
        .catch(() => {});
    }

    _patchHeaderCount(cart) {
      const count = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
      document.querySelectorAll('.gg-header__count, [data-cart-count]').forEach((el) => { el.textContent = String(count); });
      document.querySelectorAll('.cart-count-bubble span[aria-hidden=true]').forEach((el) => { if (count > 0 && count < 100) el.textContent = String(count); });
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => { bubble.style.display = count > 0 ? '' : 'none'; });
    }

    _bind() {
      this.querySelectorAll('[data-cart-close]').forEach((el) => {
        el.addEventListener('click', (e) => { e.preventDefault(); this.close(); });
      });

      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });

      document.querySelectorAll('[data-cart-open]').forEach((el) => {
        el.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });

      this.addEventListener('click', (e) => {
        const swapBtn = e.target.closest('[data-pack-swap]');
        if (swapBtn) {
          e.preventDefault();
          this._swapPack(swapBtn);
          return;
        }

        const line = e.target.closest('[data-cart-line]');
        if (line) {
          if (e.target.matches('[data-qty-inc]')) {
            const input = line.querySelector('[data-qty-input]');
            const next = (parseInt(input.value, 10) || 0) + 1;
            input.value = next;
            this._debouncedUpdate(line, next);
            return;
          }
          if (e.target.matches('[data-qty-dec]')) {
            const input = line.querySelector('[data-qty-input]');
            const next = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
            input.value = next;
            this._debouncedUpdate(line, next);
            return;
          }
          if (e.target.matches('[data-cart-remove]')) {
            this._updateLine(line, 0);
            return;
          }
          const subBtn = e.target.closest('[data-sub-toggle]');
          if (subBtn) {
            e.preventDefault();
            const action = subBtn.dataset.action;
            if (action === 'upgrade') {
              this._changeSubMode(line, { planId: subBtn.dataset.subPlan });
            } else if (action === 'downgrade') {
              const row = subBtn.closest('.ggd-sub-mod');
              if (row) row.classList.add('ggd-sub-mod--shrugging');
              setTimeout(() => this._changeSubMode(line, { clearPlan: true }), 300);
            }
            return;
          }
        }

        if (e.target.matches('[data-remove-discount]')) {
          e.preventDefault();
          this._removeDiscount(e.target);
        }
      });

      this.addEventListener('change', (e) => {
        if (!e.target.matches('[data-qty-input]')) return;
        const line = e.target.closest('[data-cart-line]');
        if (!line) return;
        let q = parseInt(e.target.value, 10);
        if (isNaN(q) || q < 0) q = 0;
        this._updateLine(line, q);
      });

      const form = this.querySelector('[data-discount-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const input = this.querySelector('[data-discount-input]');
          const code = (input.value || '').trim().toUpperCase();
          if (!code) return;
          try { sessionStorage.setItem('gg_discount_attempt', code); sessionStorage.setItem('gg_open_drawer', '1'); } catch (_) {}
          window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
        });
      }

      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        const action = form.getAttribute('action') || '';
        if (!action.match(/\/cart\/add/i)) return;
        e.preventDefault();
        const fd = new FormData(form);
        const submitBtn = form.querySelector('[type=submit]');
        if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');

        this.classList.remove('is-empty');
        this._openInstant({ pending: true });

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        })
          .then((r) => r.json().then((j) => ({ ok: r.ok, body: j })))
          .then(({ ok, body }) => {
            if (!ok) {
              console.warn('[cart-drawer] add failed', body);
              this._showFeedback(body?.description || 'Could not add to bag.', 'error');
              return;
            }
            return this.refresh();
          })
          .catch((err) => console.warn('[cart-drawer] add error', err))
          .finally(() => { if (submitBtn) submitBtn.removeAttribute('aria-disabled'); });
      });

      try {
        const shouldOpen = sessionStorage.getItem('gg_open_drawer');
        if (shouldOpen) {
          sessionStorage.removeItem('gg_open_drawer');
          requestAnimationFrame(() => this.open());
        }
        const attempt = sessionStorage.getItem('gg_discount_attempt');
        if (attempt) {
          sessionStorage.removeItem('gg_discount_attempt');
          const applied = this.querySelector('[data-applied-discounts]');
          const text = applied ? applied.textContent.toUpperCase() : '';
          if (text.indexOf(attempt.toUpperCase()) !== -1) {
            this._showFeedback('Code ' + attempt + ' applied.', 'success');
          } else {
            this._showFeedback('Code "' + attempt + '" not recognized.', 'error');
          }
        }
      } catch (_) {}
    }

    getSectionsToRender() { return []; }
    setActiveElement(el) { this.activeElement = el; }
    renderContents(_response) { this.classList.remove('is-empty'); this._openInstant({ pending: true }); this.refresh(); }

    open() { this._openInstant({ pending: false }); this.refresh(); }

    _openInstant({ pending }) {
      const wasOpen = this.classList.contains('is-open');
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ggd-locked', 'ggd-pushed');
      if (!wasOpen) {
        if (pending && this.body.querySelector('[data-drawer-empty]')) {
          this._renderSkeleton();
        }
      }
      if (pending) this.body.classList.add('ggd-body--loading');
      setTimeout(() => {
        const focusable = this.panel.querySelector('.ggd-close');
        if (focusable) focusable.focus();
      }, 50);
    }

    _renderSkeleton() {
      while (this.body.firstChild) this.body.removeChild(this.body.firstChild);
      const wrap = document.createElement('div');
      wrap.className = 'ggd-skeleton';
      wrap.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 1; i++) {
        const row = document.createElement('div');
        row.className = 'ggd-skeleton__row';
        wrap.appendChild(row);
      }
      this.body.appendChild(wrap);
      if (this.foot) this.foot.hidden = false;
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ggd-locked', 'ggd-pushed');
      if (this.activeElement && document.body.contains(this.activeElement)) {
        this.activeElement.focus();
      }
    }

    refresh() {
      this.body.classList.add('ggd-body--loading');
      return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          this._lastCart = cart;
          this._renderBody(cart);
          this._renderFoot(cart);
          this._patchHeaderCount(cart);
          this.body.classList.remove('ggd-body--loading');
        })
        .catch(() => { this.body.classList.remove('ggd-body--loading'); });
    }

    _renderBody(cart) {
      while (this.body.firstChild) this.body.removeChild(this.body.firstChild);
      if (!cart.items || cart.items.length === 0) {
        this.classList.add('is-empty');
        this.body.appendChild(this._buildEmpty());
        if (this.foot) this.foot.hidden = true;
        return;
      }
      this.classList.remove('is-empty');
      if (this.foot) this.foot.hidden = false;
      const ul = document.createElement('ul');
      ul.className = 'ggd-lines';
      ul.setAttribute('data-drawer-lines', '');
      cart.items.forEach((item) => ul.appendChild(this._buildLine(item)));
      this.body.appendChild(ul);
      const upsellSlot = document.createElement('div');
      upsellSlot.setAttribute('data-drawer-upsell', '');
      this.body.appendChild(upsellSlot);
      this._renderUpsell(cart, upsellSlot);
    }

    _buildEmpty() {
      const wrap = document.createElement('div');
      wrap.className = 'ggd-empty';
      wrap.setAttribute('data-drawer-empty', '');
      const stamp = document.createElement('span');
      stamp.className = 'ggd-empty__stamp';
      stamp.textContent = "Bag's empty.";
      wrap.appendChild(stamp);
      const h = document.createElement('h3');
      h.className = 'ggd-empty__h';
      h.textContent = "Let's fix that.";
      wrap.appendChild(h);
      const p = document.createElement('p');
      p.className = 'ggd-empty__p';
      p.textContent = "Pick your bag. We'll have it on its way before your next workout.";
      wrap.appendChild(p);
      const cta = document.createElement('a');
      cta.className = 'ggd-empty__cta';
      cta.href = '/products/gummy-gainz-protein-candy';
      cta.textContent = 'Shop the bag';
      wrap.appendChild(cta);
      return wrap;
    }

    _buildLine(item) {
      const li = document.createElement('li');
      li.className = 'ggd-line';
      li.setAttribute('data-cart-line', '');
      li.setAttribute('data-line-key', item.key);
      if (item.image) {
        const a = document.createElement('a');
        a.href = item.url;
        a.tabIndex = -1;
        a.setAttribute('aria-hidden', 'true');
        const img = document.createElement('img');
        img.className = 'ggd-line__img';
        img.src = this._resize(item.image, 160);
        img.alt = item.product_title || '';
        img.width = 72;
        img.height = 72;
        a.appendChild(img);
        li.appendChild(a);
      } else {
        const ph = document.createElement('span');
        ph.className = 'ggd-line__img';
        li.appendChild(ph);
      }
      const details = document.createElement('div');
      const title = document.createElement('a');
      title.className = 'ggd-line__title';
      title.href = item.url;
      title.textContent = item.product_title;
      details.appendChild(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const v = document.createElement('p');
        v.className = 'ggd-line__variant';
        v.textContent = item.variant_title;
        details.appendChild(v);
      }
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        const plan = document.createElement('span');
        plan.className = 'ggd-line__plan';
        plan.textContent = item.selling_plan_allocation.selling_plan.name;
        details.appendChild(plan);
      }
      const bottom = document.createElement('div');
      bottom.className = 'ggd-line__bottom';
      const qty = document.createElement('div');
      qty.className = 'ggd-qty';
      const dec = document.createElement('button');
      dec.type = 'button';
      dec.className = 'ggd-qty__btn';
      dec.setAttribute('data-qty-dec', '');
      dec.setAttribute('aria-label', 'Decrease quantity');
      dec.textContent = '−';
      qty.appendChild(dec);
      const input = document.createElement('input');
      input.type = 'number';
      input.className = 'ggd-qty__input';
      input.setAttribute('data-qty-input', '');
      input.value = String(item.quantity);
      input.min = '0';
      input.setAttribute('aria-label', 'Quantity for ' + item.product_title);
      qty.appendChild(input);
      const inc = document.createElement('button');
      inc.type = 'button';
      inc.className = 'ggd-qty__btn';
      inc.setAttribute('data-qty-inc', '');
      inc.setAttribute('aria-label', 'Increase quantity');
      inc.textContent = '+';
      qty.appendChild(inc);
      bottom.appendChild(qty);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ggd-remove';
      remove.setAttribute('data-cart-remove', '');
      remove.textContent = 'Remove';
      bottom.appendChild(remove);
      details.appendChild(bottom);
      li.appendChild(details);
      const price = document.createElement('div');
      price.className = 'ggd-line__price';
      price.setAttribute('data-line-price', '');
      price.textContent = this._money(item.final_line_price);
      li.appendChild(price);
      const subMod = this._buildSubModRow(item);
      if (subMod) li.appendChild(subMod);
      return li;
    }

    static get SUB_PLAN_MAP() {
      return {
        46093837762801: { plan: 5588582641, price: 3000, oneTime: 3300 },
        46093838418161: { plan: 5588615409, price: 8500, oneTime: 10000 },
      };
    }

    static get PACK_VARIANTS() {
      return ['46108914483441', '46093837762801', '46093838418161'];
    }

    static get PACK_META() {
      return {
        '46108914483441': { variantTitle: 'The Spectator - 1 Pouch / Strawberry Lemonade', price: 1200 },
        '46093837762801': { variantTitle: 'The Weekend Warrior - 3 Pouches / Strawberry Lemonade', price: 3300 },
        '46093838418161': { variantTitle: 'The Contender - 10 Pouches / Strawberry Lemonade', price: 10000 },
      };
    }

    _buildSubModRow(item) {
      const planInfo = CartDrawer.SUB_PLAN_MAP[item.variant_id];
      if (!planInfo) return null;
      const isSub = !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
      const row = document.createElement('div');
      row.className = 'ggd-sub-mod' + (isSub ? ' ggd-sub-mod--active' : '');
      row.setAttribute('data-sub-mod', '');

      const label = document.createElement('div');
      label.className = 'ggd-sub-mod__label';
      const lead = document.createElement('span');
      lead.className = 'ggd-sub-mod__lead';
      const sub = document.createElement('span');
      sub.className = 'ggd-sub-mod__sub';
      if (isSub) {
        lead.textContent = 'Subscribed · ships every 30 days';
        sub.textContent = 'bonus pouch on your first sub order';
      } else {
        const savings = (planInfo.oneTime - planInfo.price) / 100;
        const priceStr = (planInfo.price / 100).toFixed(0);
        lead.textContent = 'Subscribe · save $' + savings;
        sub.textContent = '$' + priceStr + '/mo · bonus pouch on your first sub order';
      }
      label.appendChild(lead);
      label.appendChild(sub);
      row.appendChild(label);

      const sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'ggd-sub-mod__switch' + (isSub ? ' ggd-sub-mod__switch--on' : '');
      sw.setAttribute('data-sub-toggle', '');
      sw.setAttribute('data-action', isSub ? 'downgrade' : 'upgrade');
      sw.setAttribute('data-sub-plan', planInfo.plan);
      sw.setAttribute('role', 'switch');
      sw.setAttribute('aria-checked', isSub ? 'true' : 'false');
      sw.setAttribute('aria-label', isSub ? 'Cancel subscription' : 'Subscribe and save');
      const knob = document.createElement('span');
      knob.className = 'ggd-sub-mod__knob';
      sw.appendChild(knob);
      row.appendChild(sw);

      return row;
    }

    _changeSubMode(line, options) {
      const willBeSub = !!options.planId;
      const row = line.querySelector('[data-sub-mod]');
      const sw = row && row.querySelector('[data-sub-toggle]');
      if (sw) {
        sw.classList.toggle('ggd-sub-mod__switch--on', willBeSub);
        sw.setAttribute('aria-checked', String(willBeSub));
      }
      if (row) row.classList.toggle('ggd-sub-mod--active', willBeSub);
      const item = this._lastCart && (this._lastCart.items || []).find((i) => i.key === line.dataset.lineKey);
      if (item) {
        const planInfo = CartDrawer.SUB_PLAN_MAP[item.variant_id];
        if (planInfo) {
          const priceEl = line.querySelector('[data-line-price]');
          if (priceEl) priceEl.textContent = this._money(willBeSub ? planInfo.price : planInfo.oneTime);
        }
      }
      if (options.clearPlan) {
        this._showCartToast('alright, one-time it is. you can always come back. 🙂');
      }

      const key = line.dataset.lineKey;
      const body = { id: key, quantity: 1 };
      if (options.planId) {
        body.selling_plan = String(options.planId);
      } else if (options.clearPlan) {
        body.selling_plan = '';
      }
      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((cart) => {
          this._lastCart = cart;
          this._renderBody(cart);
          this._renderFoot(cart);
          this._patchHeaderCount(cart);
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(() => { this.refresh(); });
    }

    _swapPack(button) {
      const variantId = button.dataset.variant;
      const planId = button.dataset.plan || null;
      const toastMessage = button.dataset.toast || '';
      if (!variantId) return;

      button.disabled = true;

      const cart = this._lastCart || { items: [] };
      const linesToRemove = (cart.items || [])
        .filter((item) =>
          CartDrawer.PACK_VARIANTS.indexOf(String(item.variant_id)) !== -1 &&
          String(item.variant_id) !== String(variantId) &&
          (planId ? true : !item.selling_plan_allocation)
        )
        .map((item) => item.key);

      if (toastMessage) this._showCartToast(toastMessage);

      const newMeta = CartDrawer.PACK_META[variantId];
      let morphedLineEl = null;
      if (!planId && newMeta && linesToRemove.length > 0) {
        const oldKey = linesToRemove[0];
        const safeKey = (window.CSS && CSS.escape) ? CSS.escape(oldKey) : oldKey.replace(/"/g, '\\"');
        morphedLineEl = this.querySelector('[data-line-key="' + safeKey + '"]');
        if (morphedLineEl) {
          const vEl = morphedLineEl.querySelector('.ggd-line__variant');
          if (vEl) vEl.textContent = newMeta.variantTitle;
          const pEl = morphedLineEl.querySelector('[data-line-price]');
          if (pEl) pEl.textContent = this._money(newMeta.price);
          const subModOld = morphedLineEl.querySelector('[data-sub-mod]');
          if (subModOld) subModOld.remove();
          const syntheticItem = { variant_id: parseInt(variantId, 10), selling_plan_allocation: null };
          const newSubMod = this._buildSubModRow(syntheticItem);
          if (newSubMod) morphedLineEl.appendChild(newSubMod);
          const totalEl = this.foot && this.foot.querySelector('[data-cart-total]');
          if (totalEl) totalEl.textContent = this._money(newMeta.price);
          this._patchHeaderCount({ item_count: 1 });
          const upsellSlotEarly = this.body.querySelector('[data-drawer-upsell]');
          if (upsellSlotEarly) {
            const syntheticCart = { items: [{ variant_id: parseInt(variantId, 10), final_line_price: newMeta.price }], total_price: newMeta.price };
            this._renderUpsell(syntheticCart, upsellSlotEarly);
          }
        }
      }

      const addPayload = planId
        ? { id: variantId, quantity: 1, selling_plan: planId }
        : { id: variantId, quantity: 1 };
      const addPromise = fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addPayload),
      }).then((r) => r.json().then((j) => ({ ok: r.ok, body: j })));

      const removePromise = linesToRemove.length
        ? (function () {
            const updates = {};
            linesToRemove.forEach((key) => { updates[key] = 0; });
            return fetch('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: updates }),
            }).then((r) => r.json());
          })()
        : Promise.resolve(null);

      Promise.all([addPromise, removePromise])
        .then((results) => {
          const addResult = results[0];
          if (!addResult.ok) throw new Error((addResult.body && addResult.body.description) || 'add failed');
          return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
        })
        .then((newCart) => {
          this._lastCart = newCart;
          if (morphedLineEl) {
            const newLine = (newCart.items || []).find((i) => String(i.variant_id) === String(variantId) && !i.selling_plan_allocation);
            const wrongLinesStillPresent = (newCart.items || []).some((i) =>
              CartDrawer.PACK_VARIANTS.indexOf(String(i.variant_id)) !== -1 &&
              String(i.variant_id) !== String(variantId) &&
              !i.selling_plan_allocation
            );
            if (newLine && !wrongLinesStillPresent) {
              morphedLineEl.setAttribute('data-line-key', newLine.key);
              const pEl = morphedLineEl.querySelector('[data-line-price]');
              if (pEl) pEl.textContent = this._money(newLine.final_line_price);
              this._renderFoot(newCart);
              this._patchHeaderCount(newCart);
              const upsellSlot = this.body.querySelector('[data-drawer-upsell]');
              if (upsellSlot) this._renderUpsell(newCart, upsellSlot);
            } else {
              this._renderBody(newCart);
              this._renderFoot(newCart);
              this._patchHeaderCount(newCart);
            }
          } else {
            this._renderBody(newCart);
            this._renderFoot(newCart);
            this._patchHeaderCount(newCart);
          }
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(() => {
          button.disabled = false;
          this.refresh();
        });
    }

    _showCartToast(message) {
      let toast = this.panel ? this.panel.querySelector('[data-cart-toast]') : null;
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'ggd-toast';
        toast.setAttribute('data-cart-toast', '');
        (this.panel || this).appendChild(toast);
      }
      toast.textContent = message;
      toast.offsetHeight;
      toast.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { toast.classList.remove('is-visible'); }, 3200);
    }

    _renderUpsell(cart, slot) {
      while (slot.firstChild) slot.removeChild(slot.firstChild);
      if (!cart.items || cart.items.length === 0) return;

      const has10 = cart.items.some((i) => String(i.variant_id) === '46093838418161');
      const sub3 = cart.items.some((i) => String(i.variant_id) === '46093837762801' && i.selling_plan_allocation);
      const anySub = cart.items.some((i) => i.selling_plan_allocation);

      let copy = null;
      let cta = null;
      let swapVariant = null;
      let swapPlan = null;
      let toast = null;

      if (sub3 && !has10) {
        // Subscriber on the 3-pack: the 10-pack subscription ships free and is cheaper per pouch.
        copy = 'Heads up: the 10-pack subscription ships free. Comes out to $8.50 a pouch.';
        cta = 'Make it the 10-pack';
        swapVariant = '46093838418161';
        swapPlan = '5588615409';
        toast = 'done. free shipping, $8.50 a pouch.';
      } else if (!anySub) {
        const total = cart.total_price;
        if (total <= 1500) {
          copy = 'One pouch is a snack. Three is a tournament weekend handled.';
          cta = 'Make it the 3-pack';
          swapVariant = '46093837762801';
          toast = 'now you’re set for the weekend.';
        } else if (total <= 4000 && !has10) {
          copy = 'Heads up: the 10-pack ships free. Comes out to $10 a pouch.';
          cta = 'Make it the 10-pack';
          swapVariant = '46093838418161';
          toast = 'done. free shipping on the way.';
        }
      }
      if (!copy) return;

      const box = document.createElement('div');
      box.className = 'ggd-upsell';
      const p = document.createElement('p');
      p.className = 'ggd-upsell__text';
      p.textContent = copy;
      box.appendChild(p);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ggd-upsell__cta';
      btn.setAttribute('data-pack-swap', '');
      btn.setAttribute('data-variant', swapVariant);
      if (swapPlan) btn.setAttribute('data-plan', swapPlan);
      btn.setAttribute('data-toast', toast);
      btn.textContent = cta;
      box.appendChild(btn);
      slot.appendChild(box);
    }

    _renderFoot(cart) {
      if (!this.foot) return;
      const totalEl = this.foot.querySelector('[data-cart-total]');
      if (totalEl) totalEl.textContent = this._money(cart.total_price);
      const totals = this.foot.querySelector('[data-drawer-totals]');
      if (totals) {
        while (totals.firstChild) totals.removeChild(totals.firstChild);
        if (cart.total_discount > 0) {
          totals.appendChild(this._totalsRow('Subtotal before discount', this._money(cart.original_total_price || (cart.total_price + cart.total_discount)), 'ggd-totals__row--strike'));
          totals.appendChild(this._totalsRow('Discount', '−' + this._money(cart.total_discount), 'ggd-totals__row--discount'));
        }
        const sub = this._totalsRow('Subtotal', this._money(cart.total_price), 'ggd-totals__row--sub');
        sub.querySelectorAll('span')[1].classList.add('ggd-totals__val');
        sub.querySelectorAll('span')[1].setAttribute('data-cart-total', '');
        totals.appendChild(sub);
      }
      const oldApplied = this.foot.querySelector('[data-applied-discounts]');
      if (oldApplied) oldApplied.parentNode.removeChild(oldApplied);
      const codes = this._collectAppliedCodes(cart);
      if (codes.length) {
        const appliedWrap = document.createElement('div');
        appliedWrap.className = 'ggd-applied';
        appliedWrap.setAttribute('data-applied-discounts', '');
        codes.forEach((code) => {
          const row = document.createElement('div');
          row.className = 'ggd-applied__row';
          const left = document.createElement('span');
          const pill = document.createElement('span');
          pill.className = 'ggd-applied__pill';
          pill.textContent = code;
          left.appendChild(pill);
          left.appendChild(document.createTextNode('applied'));
          row.appendChild(left);
          const rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'ggd-remove';
          rm.setAttribute('data-remove-discount', '');
          rm.textContent = 'Remove';
          row.appendChild(rm);
          appliedWrap.appendChild(row);
        });
        const totalsEl = this.foot.querySelector('[data-drawer-totals]');
        this.foot.insertBefore(appliedWrap, totalsEl);
      }
    }

    _totalsRow(label, value, modifier) {
      const row = document.createElement('div');
      row.className = 'ggd-totals__row' + (modifier ? ' ' + modifier : '');
      const l = document.createElement('span');
      l.textContent = label;
      const v = document.createElement('span');
      v.textContent = value;
      if (modifier === 'ggd-totals__row--strike') v.className = 'ggd-totals__row--strike';
      row.appendChild(l);
      row.appendChild(v);
      return row;
    }

    _collectAppliedCodes(cart) {
      const set = new Set();
      (cart.cart_level_discount_applications || []).forEach((a) => set.add(a.title));
      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((alloc) => {
          if (alloc.discount_application && alloc.discount_application.title) {
            set.add(alloc.discount_application.title);
          }
        });
      });
      return Array.from(set);
    }

    _debouncedUpdate(line, qty) {
      this._optimisticUpdate(line, qty);
      clearTimeout(line._debounce);
      line._debounce = setTimeout(() => this._updateLine(line, qty), 220);
    }

    _optimisticUpdate(line, quantity) {
      if (!this._lastCart) return;
      const key = line.dataset.lineKey;
      const item = (this._lastCart.items || []).find((i) => i.key === key);
      if (!item || !item.quantity) return;
      const unit = item.final_line_price / item.quantity;
      const nextLineTotal = Math.round(unit * Math.max(0, quantity));
      const priceEl = line.querySelector('[data-line-price]');
      if (priceEl) priceEl.textContent = this._money(nextLineTotal);
      const totalEl = this.foot && this.foot.querySelector('[data-cart-total]');
      if (totalEl) {
        let sum = 0;
        this.querySelectorAll('[data-cart-line]').forEach((l) => {
          if (l === line) { sum += nextLineTotal; }
          else {
            const k = l.dataset.lineKey;
            const it = (this._lastCart.items || []).find((i) => i.key === k);
            if (it) sum += it.final_line_price;
          }
        });
        totalEl.textContent = this._money(sum);
      }
      let qtySum = 0;
      this.querySelectorAll('[data-cart-line]').forEach((l) => {
        const input = l.querySelector('[data-qty-input]');
        qtySum += Math.max(0, parseInt(input ? input.value : '0', 10) || 0);
      });
      this._patchHeaderCount({ item_count: qtySum });
    }

    _updateLine(line, quantity) {
      this._setBusy(line, true);
      const key = line.dataset.lineKey;
      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
      })
        .then((r) => r.json())
        .then((cart) => {
          this._lastCart = cart;
          this._renderBody(cart);
          this._renderFoot(cart);
          this._patchHeaderCount(cart);
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(() => { this._setBusy(line, false); this.refresh(); });
    }

    _setBusy(line, busy) {
      line.style.opacity = busy ? '0.5' : '1';
      line.style.pointerEvents = busy ? 'none' : '';
    }

    _removeDiscount(btn) {
      btn.disabled = true;
      btn.textContent = 'Removing…';
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      })
        .then((r) => r.json())
        .then((cart) => {
          this._lastCart = cart;
          this._renderBody(cart);
          this._renderFoot(cart);
          this._patchHeaderCount(cart);
        })
        .catch(() => { btn.disabled = false; btn.textContent = 'Remove'; });
    }

    _showFeedback(message, type) {
      const slot = this.querySelector('[data-discount-feedback]');
      if (!slot) return;
      while (slot.firstChild) slot.removeChild(slot.firstChild);
      const box = document.createElement('div');
      box.className = 'ggd-feedback__box ggd-feedback__box--' + type;
      box.textContent = message;
      slot.appendChild(box);
    }

    _money(cents) { if (typeof cents !== 'number') return ''; return '$' + (cents / 100).toFixed(2); }

    _resize(url, width) { if (!url) return ''; return url.replace(/(\.[^.?]+)(\?.*)?$/, '_' + width + 'x$1$2'); }
  }

  customElements.define('cart-drawer', CartDrawer);
}
